import React, { useState } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { Menu, X, Search, LogOut } from 'lucide-react';
import { Logo } from './Logo';
import { useAuth } from '../../state/auth';
import { authApi } from '../../api/auth';

const NAV = [
  { to: '/', label: 'Dashboard', end: true },
  { to: '/themas', label: 'Thema’s', end: false },
  { to: '/voortgang', label: 'Mijn voortgang', end: false },
  { to: '/certificaten', label: 'Certificaten', end: false },
];

const linkClass = ({ isActive }: { isActive: boolean }) =>
  `text-sm font-medium transition-colors ${isActive ? 'text-ink' : 'text-sub hover:text-ink'}`;

const initials = (name: string) => name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]?.toUpperCase()).join('') || '•';

const AuthArea: React.FC = () => {
  const { user, providers, loading, logout } = useAuth();
  const [open, setOpen] = useState(false);
  if (loading) return <span className="w-[34px] h-[34px]" />;

  if (user) {
    return (
      <div className="relative">
        <button onClick={() => setOpen((v) => !v)} className="grid place-items-center rounded-full text-white text-xs font-semibold" style={{ width: 34, height: 34, background: '#2440CF' }} aria-label="Account">
          {initials(user.name)}
        </button>
        {open && (
          <div className="absolute right-0 mt-2 card shadow-hover p-1 min-w-[180px] z-50">
            <p className="px-3 py-2 text-sm text-ink truncate">{user.name}</p>
            <button onClick={() => { setOpen(false); logout(); }} className="flex items-center gap-2 w-full text-left px-3 py-2 text-sm text-sub hover:bg-canvas rounded">
              <LogOut className="w-4 h-4" strokeWidth={1.75} /> Uitloggen
            </button>
          </div>
        )}
      </div>
    );
  }

  if (!providers.length) return null; // geen SSO geconfigureerd → anoniem

  return (
    <div className="relative">
      <button onClick={() => setOpen((v) => !v)} className="btn-primary" style={{ padding: '8px 14px', fontSize: 13 }}>Inloggen</button>
      {open && (
        <div className="absolute right-0 mt-2 card shadow-hover p-1 min-w-[200px] z-50">
          {providers.map((p) => (
            <a key={p.id} href={authApi.loginUrl(p.id)} className="block px-3 py-2 text-sm text-ink hover:bg-canvas rounded">Inloggen met {p.label}</a>
          ))}
        </div>
      )}
    </div>
  );
};

export const Topbar: React.FC = () => {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  return (
    <header className="sticky top-0 z-50 border-b border-hairline" style={{ background: 'rgba(255,255,255,.92)', backdropFilter: 'saturate(140%) blur(8px)' }}>
      <div className="shell flex items-center justify-between gap-4" style={{ paddingTop: 13, paddingBottom: 13 }}>
        <div className="flex items-center" style={{ gap: 'clamp(20px,4vw,40px)' }}>
          <Link to="/" aria-label="DigitalNext — naar dashboard"><Logo /></Link>
          <nav className="hidden md:flex items-center gap-7">
            {NAV.map((n) => (<NavLink key={n.to} to={n.to} end={n.end} className={linkClass}>{n.label}</NavLink>))}
          </nav>
        </div>

        <div className="flex items-center gap-3">
          <form className="hidden md:flex items-center gap-2 bg-canvas border border-hairline rounded px-3 py-2" onSubmit={(e) => { e.preventDefault(); navigate('/themas'); }}>
            <Search className="w-4 h-4 text-muted" strokeWidth={1.75} />
            <input className="bg-transparent outline-none text-sm text-ink placeholder:text-muted w-40" placeholder="Zoek een thema…" />
          </form>
          <AuthArea />
          <button className="md:hidden text-ink p-1" aria-label="Menu" onClick={() => setOpen((v) => !v)}>
            {open ? <X className="w-6 h-6" strokeWidth={1.75} /> : <Menu className="w-6 h-6" strokeWidth={1.75} />}
          </button>
        </div>
      </div>

      {open && (
        <nav className="md:hidden border-t border-hairline bg-surface">
          <div className="shell py-2 flex flex-col">
            {NAV.map((n) => (
              <NavLink key={n.to} to={n.to} end={n.end} onClick={() => setOpen(false)}
                className={({ isActive }) => `py-3 text-sm font-medium ${isActive ? 'text-ink' : 'text-sub'}`}>{n.label}</NavLink>
            ))}
          </div>
        </nav>
      )}
    </header>
  );
};
